import { Accordion, Box, Group } from "@mantine/core";
import { useLocalStorage } from "@mantine/hooks";
import { IconGraph, IconTable } from "@tabler/icons-react";
import { type DateTime } from "luxon";
import { useEffect, useState } from "react";
import type { DataGaps } from "../../../../../../../utils/backend-types";
import type { Frequency, Unit } from "../../../../../../../utils/enums";
import {
  DATA_GAPS_PREFERENCE,
  DATA_GAPS_PREFERENCE_CHART,
  DATA_GAPS_PREFERENCE_TABLE
} from "../../../../../../../utils/sessionStorageKeys.constant";
import { LoadOrError } from "../../../../../../LoadOrError/LoadOrError";
import { useLocationDataGaps } from "../hooks/useLocationDataGaps";
import { useMeterDataGaps } from "../hooks/useMeterDataGaps";
import type { DataGapsTableObject } from "./DataGaps.types";
import { DataGapsChart } from "./DataGapsChart/DataGapsChart";
import { DataGapsTable } from "./DataGapsTable/DataGapsTable";
import { getTableDataFromDataGaps } from "./utils/getTableDataFromDataGaps";

interface DataGapsProps {
  frequency: Frequency;
  selectedDateRange: [DateTime<true>, DateTime<true>];
  name?: string;
}

interface DataGapsMeterProps extends DataGapsProps {
  type: "meter";
  meterId: number;
  unit: Unit;
}

interface DataGapsLocationProps extends DataGapsProps {
  type: "location";
  locationId: number;
}

type DataGapsViewProps = DataGapsMeterProps | DataGapsLocationProps;

function DataGapsView(props: DataGapsViewProps) {
  const { frequency, selectedDateRange, type } = props;
  const { unit, meterId } = props as DataGapsMeterProps;
  const { locationId } = props as DataGapsLocationProps;

  const [dataGapsTableData, setDataGapsTableData] = useState<
    Array<DataGapsTableObject>
  >([]);

  const [dataGapsPreference, setDataGapsPreference] = useLocalStorage({
    key: DATA_GAPS_PREFERENCE,
    defaultValue: [DATA_GAPS_PREFERENCE_CHART, DATA_GAPS_PREFERENCE_TABLE]
  });

  const {
    locationDataGaps,
    isLoading: isLoadingLocation,
    error: errorLocation
  } = useLocationDataGaps(
    type,
    locationId,
    selectedDateRange[0].toJSDate(),
    selectedDateRange[1].toJSDate()
  );

  const {
    meterDataGaps,
    isLoading: isLoadingMeter,
    error: errorMeter
  } = useMeterDataGaps(
    type,
    unit,
    meterId,
    selectedDateRange[0].toJSDate(),
    selectedDateRange[1].toJSDate()
  );

  let dataGaps: DataGaps | undefined;
  let isLoadingGaps: boolean;
  let gapsError: Error | null;

  if (type === "meter") {
    dataGaps = meterDataGaps;
    isLoadingGaps = isLoadingMeter;
    gapsError = errorMeter;
  } else {
    dataGaps = locationDataGaps;
    isLoadingGaps = isLoadingLocation;
    gapsError = errorLocation;
  }

  useEffect(() => {
    if (dataGaps) {
      setDataGapsTableData(getTableDataFromDataGaps(dataGaps, frequency));
    } else {
      setDataGapsTableData([]);
    }
  }, [dataGaps, frequency]);

  return (
    <Box className="DataGaps" mt="lg">
      <LoadOrError error={gapsError} loading={isLoadingGaps ?? true}>
        {dataGaps && (
          <Accordion
            multiple
            value={dataGapsPreference}
            variant="separated"
            onChange={setDataGapsPreference}
          >
            <Accordion.Item value={DATA_GAPS_PREFERENCE_CHART}>
              <Accordion.Control>
                <Group gap="xs">
                  <IconGraph size="1.3rem" />
                  Datenverfügbarkeit
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <DataGapsChart
                  dataGaps={dataGaps}
                  frequency={frequency}
                  selectedTimeRange={selectedDateRange}
                />
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value={DATA_GAPS_PREFERENCE_TABLE}>
              <Accordion.Control>
                <Group gap="xs">
                  <IconTable size="1.3rem" />
                  Datenlücken
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <DataGapsTable data={dataGapsTableData} frequency={frequency} />
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        )}
      </LoadOrError>
    </Box>
  );
}

export { DataGapsView };
